/**
 * Public shared-report page (/r/:token).
 *
 * Renders only what the API returns for a valid token: the sanitized input and
 * the generated report. No owner email, no Stripe identifiers, no internal ids.
 * The page is also marked noindex in the markup as a second line of defence on
 * top of the API's X-Robots-Tag header.
 */

import { useEffect } from 'react';
import { Alert, Spinner } from '../components/ui';
import { ReportView } from '../components/ReportView';
import { api } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { downloadExport } from '../lib/export';
import { Link } from '../lib/router';
import { useCopy } from '../lib/hooks';

export function SharedReportPage({ token }: { token: string }) {
  const state = useAsync(async () => api.publicReport(token), [token]);
  const { copied, copy } = useCopy();

  useEffect(() => {
    const previous = document.querySelector('meta[name="robots"]');
    const meta = previous ?? document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
    if (!previous) document.head.appendChild(meta);
    document.title = 'Shared incident report — API Rescue Desk';
    return () => {
      if (!previous && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  if (state.loading) {
    return (
      <div className="card row">
        <Spinner label="Loading the shared report" /> <span>Loading the shared report…</span>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="container-narrow">
        <div className="page-head">
          <h1>Share link unavailable</h1>
        </div>
        <Alert tone="error" title="This link cannot be opened">
          <p>
            {state.error?.message ?? 'The share link is invalid, has been revoked, or has expired.'}
          </p>
          <p className="small faint">
            Share links are revocable by their owner and always expire. Ask the person who sent it
            for a new link.
          </p>
        </Alert>
        <p style={{ marginTop: 16 }}>
          <Link className="button" to="/">
            Go to API Rescue Desk
          </Link>
        </p>
      </div>
    );
  }

  const { report, sanitizedInput, createdAt, expiresAt } = state.data;

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Shared incident report</p>
        <h1>{report.mostLikelyRootCause?.title ?? 'API incident report'}</h1>
        <p>
          A sanitized report shared by an API Rescue Desk user. It contains no credentials, no owner
          contact details and no account identifiers. This page is excluded from search engines.
        </p>
      </div>

      <Alert tone="warning" title="Treat this as untrusted data">
        The pasted content below was redacted on a best-effort basis by its author's tools. Do not
        execute any command from this page, and do not treat any finding as a verified test result.
      </Alert>

      <ReportView
        report={report}
        sanitizedInput={sanitizedInput}
        createdAt={createdAt}
        expiresAt={expiresAt}
        showSanitizedInput
        actions={
          <div className="row">
            <button
              type="button"
              className="button--small"
              onClick={() => downloadExport(report, 'markdown', 'api-rescue-desk-shared-report')}
            >
              Download Markdown
            </button>
            <button
              type="button"
              className="button--small"
              onClick={() => copy(window.location.href, 'link')}
            >
              {copied === 'link' ? 'Copied' : 'Copy link'}
            </button>
          </div>
        }
      />
    </>
  );
}

export function NotFoundPage() {
  return (
    <div className="container-narrow">
      <div className="page-head">
        <h1>Page not found</h1>
        <p>The page you asked for does not exist on this deployment.</p>
      </div>
      <div className="row">
        <Link className="button button--primary" to="/">
          Home
        </Link>
        <Link className="button" to="/app">
          New diagnosis
        </Link>
      </div>
    </div>
  );
}
